import React, { useState, useEffect, useCallback } from 'react'
import { gql } from 'graphql-request'
import { useTranslation } from 'react-i18next'
import { authorizedGraphQLRequest } from '@/api/graphqlClient'
import { X, Link2, Copy, Check, Ban, AlertCircle, Users, Plus, ArrowRightLeft, Sparkles } from 'lucide-react'
import { Button } from '@/components/ui/button'
import type { OrganisationWithDetails } from '../index'

interface JoinLinkModule {
  id: number
  name: string
  position: number
}

interface JoinLinkRedemption {
  id: number
  userId: number
  email: string
  firstName: string | null
  lastName: string | null
  redeemedAt: string
  createdAccount: boolean
  previousOrganisationId: number | null
  previousOrganisationName: string | null
}

interface JoinLink {
  id: number
  token: string
  organisationId: number
  isActive: boolean
  expiresAt: string | null
  createdAt: string
  revokedAt: string | null
  createdByEmail: string | null
  modules: JoinLinkModule[]
  totalRedemptions: number
  distinctUsers: number
  /** null = no ceiling on the accounts this link may create. */
  maxNewAccounts: number | null
  /** Accounts the server created through this link; what the ceiling is measured against. */
  newAccountCount: number
  redemptions: JoinLinkRedemption[]
}

interface JoinLinksModalProps {
  isOpen: boolean
  onClose: () => void
  organisation: OrganisationWithDetails
  /** What a link may cover: subscribed modules for B2B, active B2C modules for B2C. */
  modules: { id: number; name: string }[]
  /** Opened from a module card — that module starts selected. */
  preselectedModuleId?: number | null
}

const GET_ORGANISATION_JOIN_LINKS = gql`
  query getOrganisationJoinLinks($organisationId: Int!) {
    getOrganisationJoinLinks(organisationId: $organisationId) {
      id
      token
      organisationId
      isActive
      expiresAt
      createdAt
      revokedAt
      createdByEmail
      totalRedemptions
      distinctUsers
      maxNewAccounts
      newAccountCount
      modules {
        id
        name
        position
      }
      redemptions {
        id
        userId
        email
        firstName
        lastName
        redeemedAt
        createdAccount
        previousOrganisationId
        previousOrganisationName
      }
    }
  }
`

const CREATE_JOIN_LINK = gql`
  mutation createJoinLink($input: CreateJoinLinkInput!) {
    createJoinLink(input: $input) {
      id
      token
    }
  }
`

const REVOKE_JOIN_LINK = gql`
  mutation revokeJoinLink($joinLinkId: Int!) {
    revokeJoinLink(joinLinkId: $joinLinkId) {
      success
      message
    }
  }
`

export const joinUrlFor = (token: string) => `${window.location.origin}/auth?token=${encodeURIComponent(token)}`

const isExpired = (link: JoinLink) => !!link.expiresAt && new Date(link.expiresAt) < new Date()

const JoinLinksModal: React.FC<JoinLinksModalProps> = ({ isOpen, onClose, organisation, modules, preselectedModuleId }) => {
  const { t } = useTranslation()
  const [links, setLinks] = useState<JoinLink[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [copiedId, setCopiedId] = useState<number | null>(null)
  const [expandedId, setExpandedId] = useState<number | null>(null)
  // Selection order is meaningful: it becomes `position`, and the first grantable
  // module is where a redeemer lands.
  const [selectedModuleIds, setSelectedModuleIds] = useState<number[]>([])
  const [expiresAt, setExpiresAt] = useState('')
  const [maxNewAccounts, setMaxNewAccounts] = useState('')

  const fetchLinks = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await authorizedGraphQLRequest<{ getOrganisationJoinLinks: JoinLink[] }>(GET_ORGANISATION_JOIN_LINKS, { organisationId: organisation.id })
      setLinks(response.getOrganisationJoinLinks || [])
    } catch (err: any) {
      setError(err?.response?.errors?.[0]?.message || t('joinLinks.errorLoad'))
    } finally {
      setLoading(false)
    }
  }, [organisation.id, t])

  useEffect(() => {
    if (isOpen) {
      fetchLinks()
      setSelectedModuleIds(preselectedModuleId ? [preselectedModuleId] : [])
      setExpiresAt('')
      setMaxNewAccounts('')
    }
  }, [isOpen, preselectedModuleId, fetchLinks])

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    if (isOpen) {
      window.addEventListener('keydown', handleKeyDown)
      return () => window.removeEventListener('keydown', handleKeyDown)
    }
  }, [isOpen, onClose])

  const toggleModule = (moduleId: number) => {
    setSelectedModuleIds((current) => (current.includes(moduleId) ? current.filter((id) => id !== moduleId) : [...current, moduleId]))
  }

  const handleCreate = async () => {
    if (selectedModuleIds.length === 0) return
    setError(null)

    // Blank means no ceiling.
    const limit = maxNewAccounts.trim() === '' ? null : Number(maxNewAccounts)
    if (limit !== null && (!Number.isInteger(limit) || limit < 1)) {
      setError(t('joinLinks.maxNewAccountsInvalid'))
      return
    }

    try {
      setSubmitting(true)
      await authorizedGraphQLRequest(CREATE_JOIN_LINK, {
        input: {
          organisationId: organisation.id,
          moduleIds: selectedModuleIds,
          // Sent as end-of-day so a date picked today does not expire immediately.
          expiresAt: expiresAt ? new Date(`${expiresAt}T23:59:59`).toISOString() : null,
          maxNewAccounts: limit,
        },
      })
      setSelectedModuleIds([])
      setExpiresAt('')
      setMaxNewAccounts('')
      await fetchLinks()
    } catch (err: any) {
      setError(err?.response?.errors?.[0]?.message || t('joinLinks.errorCreate'))
    } finally {
      setSubmitting(false)
    }
  }

  const handleRevoke = async (link: JoinLink) => {
    if (!confirm(t('joinLinks.revokeConfirm'))) return
    setError(null)
    try {
      setSubmitting(true)
      await authorizedGraphQLRequest(REVOKE_JOIN_LINK, { joinLinkId: link.id })
      await fetchLinks()
    } catch (err: any) {
      setError(err?.response?.errors?.[0]?.message || t('joinLinks.errorRevoke'))
    } finally {
      setSubmitting(false)
    }
  }

  const handleCopy = async (link: JoinLink) => {
    try {
      await navigator.clipboard.writeText(joinUrlFor(link.token))
      setCopiedId(link.id)
      setTimeout(() => setCopiedId(null), 2000)
    } catch {
      setError(t('joinLinks.errorCopy'))
    }
  }

  const handleOutsideClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) onClose()
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 flex items-center justify-center p-4 z-50" onClick={handleOutsideClick}>
      <div className="fixed inset-0 bg-black/20" />

      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[85vh] flex flex-col animate-in zoom-in-95 duration-150">
        {/* Header */}
        <div className="p-6 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
          <div>
            <h2 className="text-xl font-semibold text-gray-800">{t('joinLinks.title')}</h2>
            <p className="text-sm text-gray-500 mt-1">{t('joinLinks.subtitle', { organisation: organisation.name })}</p>
          </div>
          <button onClick={onClose} className="p-1 rounded-full hover:bg-gray-100 transition-colors">
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        <div className="p-6 overflow-y-auto flex-1">
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
              <AlertCircle className="h-5 w-5 flex-shrink-0" />
              <span className="text-sm">{error}</span>
              <button onClick={() => setError(null)} className="ml-auto p-1 hover:bg-red-100 rounded">
                <X className="h-4 w-4" />
              </button>
            </div>
          )}

          {/* Create form */}
          <div className="mb-6 border border-gray-200 rounded-xl p-4">
            <h3 className="font-semibold text-gray-800 mb-1">{t('joinLinks.createTitle')}</h3>
            <p className="text-xs text-gray-500 mb-3">{t('joinLinks.selectModulesHint')}</p>

            {modules.length === 0 ? (
              <p className="text-sm text-gray-500">{t('joinLinks.noModules')}</p>
            ) : (
              <div className="flex flex-wrap gap-2 mb-4">
                {modules.map((module) => {
                  const index = selectedModuleIds.indexOf(module.id)
                  const selected = index >= 0
                  return (
                    <button
                      key={module.id}
                      onClick={() => toggleModule(module.id)}
                      className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${selected ? 'bg-primary text-white border-primary' : 'bg-white text-gray-700 border-gray-300 hover:border-primary/40'}`}
                    >
                      {selected && <span className="mr-1.5 text-xs opacity-80">{index + 1}</span>}
                      {module.name}
                    </button>
                  )
                })}
              </div>
            )}

            <div className="flex flex-wrap items-end gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">{t('joinLinks.expiry')}</label>
                <input
                  type="date"
                  value={expiresAt}
                  onChange={(e) => setExpiresAt(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">{t('joinLinks.maxNewAccounts')}</label>
                <input
                  type="number"
                  min={1}
                  step={1}
                  value={maxNewAccounts}
                  onChange={(e) => setMaxNewAccounts(e.target.value)}
                  placeholder={t('joinLinks.maxNewAccountsPlaceholder')}
                  title={t('joinLinks.maxNewAccountsHint')}
                  className="w-36 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>
              <Button onClick={handleCreate} disabled={submitting || selectedModuleIds.length === 0}>
                <Plus className="h-4 w-4 mr-1" />
                {submitting ? t('joinLinks.creating') : t('joinLinks.create')}
              </Button>
            </div>
          </div>

          {/* Existing links */}
          <h3 className="font-semibold text-gray-800 mb-3">{t('joinLinks.existing', { count: links.length })}</h3>

          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
            </div>
          ) : links.length === 0 ? (
            <div className="text-center text-gray-500 py-8 bg-gray-50 rounded-lg">
              <Link2 className="h-10 w-10 mx-auto mb-2 text-gray-300" />
              <p>{t('joinLinks.noLinks')}</p>
              <p className="text-sm mt-1">{t('joinLinks.noLinksHint')}</p>
            </div>
          ) : (
            <div className="space-y-3">
              {links.map((link) => {
                const expired = isExpired(link)
                const revoked = !link.isActive || !!link.revokedAt
                const state = revoked ? t('joinLinks.revoked') : expired ? t('joinLinks.expired') : t('joinLinks.active')
                const live = !revoked && !expired

                return (
                  <div key={link.id} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-start justify-between gap-3 mb-2">
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-1.5 mb-1.5">
                          {link.modules.map((module) => (
                            <span key={module.id} className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-blue-50 text-blue-700">
                              {module.name}
                            </span>
                          ))}
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ${live ? 'bg-green-100 text-green-800' : 'bg-gray-200 text-gray-600'}`}>
                            {state}
                          </span>
                        </div>
                        <p className="text-xs text-gray-500 truncate">{joinUrlFor(link.token)}</p>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <button onClick={() => handleCopy(link)} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors" title={t('joinLinks.copy')}>
                          {copiedId === link.id ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
                        </button>
                        {live && (
                          <button
                            onClick={() => handleRevoke(link)}
                            disabled={submitting}
                            className="p-1.5 rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-600 transition-colors disabled:opacity-50"
                            title={t('joinLinks.revoke')}
                          >
                            <Ban className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </div>

                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500">
                      <span className="inline-flex items-center gap-1">
                        <Users className="h-3.5 w-3.5" />
                        {t('joinLinks.usage', { total: link.totalRedemptions, users: link.distinctUsers })}
                      </span>
                      <span className={link.maxNewAccounts !== null && link.newAccountCount >= link.maxNewAccounts ? 'font-medium text-amber-700' : undefined}>
                        {link.maxNewAccounts !== null
                          ? t('joinLinks.newAccountsOfMax', { count: link.newAccountCount, max: link.maxNewAccounts })
                          : t('joinLinks.newAccounts', { count: link.newAccountCount })}
                      </span>
                      <span>{link.expiresAt ? t('joinLinks.expiresOn', { date: new Date(link.expiresAt).toLocaleDateString() }) : t('joinLinks.neverExpires')}</span>
                      {link.createdByEmail && <span>{t('joinLinks.createdBy', { email: link.createdByEmail })}</span>}
                      <button onClick={() => setExpandedId(expandedId === link.id ? null : link.id)} className="text-primary hover:underline">
                        {expandedId === link.id ? t('joinLinks.hideJoined') : t('joinLinks.showJoined')}
                      </button>
                    </div>

                    {expandedId === link.id && (
                      <div className="mt-3 pt-3 border-t border-gray-100">
                        {link.redemptions.length === 0 ? (
                          <p className="text-sm text-gray-500">{t('joinLinks.noRedemptions')}</p>
                        ) : (
                          <div className="space-y-2">
                            {/* Same user-row presentation as ModuleUsersModal. */}
                            {link.redemptions.map((redemption) => (
                              <div key={redemption.id} className="flex items-center justify-between bg-gray-50 p-3 rounded-lg">
                                <div className="flex items-center gap-3 min-w-0">
                                  <div className="w-10 h-10 bg-gradient-to-br from-primary to-primary/70 rounded-full flex items-center justify-center flex-shrink-0">
                                    <span className="text-white font-semibold">{(redemption.firstName?.[0] || redemption.email[0]).toUpperCase()}</span>
                                  </div>
                                  <div className="min-w-0">
                                    <p className="font-medium text-gray-800 truncate">
                                      {redemption.firstName && redemption.lastName ? `${redemption.firstName} ${redemption.lastName}` : redemption.email.split('@')[0]}
                                    </p>
                                    <p className="text-sm text-gray-500 truncate">{redemption.email}</p>
                                  </div>
                                </div>
                                <div className="flex flex-col items-end gap-1 flex-shrink-0 ml-3">
                                  <span className="text-xs text-gray-500">{new Date(redemption.redeemedAt).toLocaleString()}</span>
                                  <div className="flex items-center gap-1">
                                    {redemption.createdAccount && (
                                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-green-50 text-green-700">
                                        <Sparkles className="h-3 w-3" />
                                        {t('joinLinks.newAccount')}
                                      </span>
                                    )}
                                    {redemption.previousOrganisationName && (
                                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-amber-50 text-amber-700">
                                        <ArrowRightLeft className="h-3 w-3" />
                                        {t('joinLinks.movedFrom', { organisation: redemption.previousOrganisationName })}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          )}
        </div>

        <div className="p-6 border-t border-gray-200 flex justify-end bg-gray-50 flex-shrink-0">
          <Button variant="outline" onClick={onClose}>
            {t('joinLinks.close')}
          </Button>
        </div>
      </div>
    </div>
  )
}

export default JoinLinksModal
